import { produce } from "immer";
import React from "react";
import styled, { css } from "styled-components";
import { apiPost } from "../../api";
import { narrowScreenMediaQuery, orangish } from "../../constants/styles";
import Popupable from "../../components/Popupable";
import HoveredAlert, { ALERT_INFO } from "../../components/HoveredAlert";
import Alert from "../../components/Alert";
import { AffiliatorMyInfoContext } from "../../providers/ProvideAffiliatorMyInfo";
import { BalanceLogContext } from "./ProvideBalanceLog";
import { PopupableContext } from "../../providers/ProvidePopupable";
import { unmodule, addThousandSeparator } from "../../util";

const ACTIVE = 0;
const SUBMITTING = 1;
const FAILED = 2;
const SUCCEED = 3;

const REFRESH_COOLDOWNS_SEC = [60, 120, 180, 300, 600];
const REFRESH_STORAGE_KEY = "affiliator_withdrawal_refresh_state";
const REFRESH_SESSION_TTL_MS = 60 * 60 * 1000;

const readRefreshState = () => {
  try {
    const raw = window.localStorage.getItem(REFRESH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.nextAllowedAt !== "number") return null;
    if (
      typeof parsed.lastAttemptAt === "number" &&
      Date.now() - parsed.lastAttemptAt > REFRESH_SESSION_TTL_MS
    ) {
      window.localStorage.removeItem(REFRESH_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeRefreshState = (state) => {
  try {
    window.localStorage.setItem(REFRESH_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private mode, quota); the rate limit
    // still works server-side, so swallow the failure.
  }
};

const UnnamedComponent = (props) => {
  const { myInfo, reloadMyInfo } = React.use(AffiliatorMyInfoContext);
  const { fetchData } = React.use(BalanceLogContext);
  const { closeModal } = React.use(PopupableContext);

  const [withdrawState, setWithdrawState] = React.useState({});
  const [submitState, setSubmitState] = React.useState(ACTIVE);
  const [withdrawErrorMsg, setWithdrawErrorMsg] = React.useState();
  const [refreshLoading, setRefreshLoading] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState("");
  const [refreshCooldownUntil, setRefreshCooldownUntil] = React.useState(() => {
    const persisted = readRefreshState();
    return persisted && persisted.nextAllowedAt > Date.now()
      ? persisted.nextAllowedAt
      : null;
  });
  const [refreshTick, setRefreshTick] = React.useState(0);

  React.useEffect(() => {
    if (!refreshCooldownUntil) return undefined;
    const interval = window.setInterval(() => {
      if (Date.now() >= refreshCooldownUntil) {
        window.clearInterval(interval);
        setRefreshCooldownUntil(null);
      } else {
        setRefreshTick((t) => t + 1);
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [refreshCooldownUntil]);

  const refreshSecondsLeft = React.useMemo(() => {
    if (!refreshCooldownUntil) return 0;
    return Math.max(0, Math.ceil((refreshCooldownUntil - Date.now()) / 1000));
  }, [refreshCooldownUntil, refreshTick]);

  const isRefreshInCooldown = refreshSecondsLeft > 0;
  const withdrawAmounts = React.useMemo(
    () => [
      {
        amount: 500000,
        disabled: myInfo.balance_amount < 500000,
        label: "Rp500.000,-",
        $active: !!withdrawState?.["500000"],
      },
      {
        amount: 1000000,
        disabled: myInfo.balance_amount < 1000000,
        label: "Rp1.000.000,-",
        $active: !!withdrawState?.["1000000"],
      },
      {
        amount: 1500000,
        disabled: myInfo.balance_amount < 1500000,
        label: "Rp1.500.000,-",
        $active: !!withdrawState?.["1500000"],
      },
    ],
    [myInfo, withdrawState],
  );
  const withdrawSelectedAmount = React.useMemo(() => {
    for (const k in withdrawState) {
      if (k !== "custom") {
        if (withdrawState[k]) {
          const val = parseFloat(k);
          if (val > 0 && val <= myInfo.balance_amount) {
            return val;
          }
        }
      } else {
        if (withdrawState[k]) {
          const value = parseFloat(withdrawState[k].replaceAll(/\./g, ""));
          if (value % 500000 === 0) {
            if (value > 0 && value <= myInfo.balance_amount) {
              return value;
            }
          }
        }
      }
    }
    return false;
  }, [withdrawState]);

  const handleChangeWithdrawAmount = React.useCallback((evt) => {
    const { value } = evt.target;
    setWithdrawState(
      // (x) => {}
      produce((draft) => {
        if (parseFloat(value) > 0) {
          draft.custom = addThousandSeparator(value);
        }
      }),
    );
  }, []);
  const handleWithdrawInsideClick = React.useCallback(
    async (e) => {
      setSubmitState(SUBMITTING);

      const { parsedResponse, statusCode } = await apiPost(
        "/api/v1/users/affiliators/withdraw",
        {
          body: {
            affiliator_withdrawal: {
              price: withdrawSelectedAmount,
            },
          },
        },
      );
      if (statusCode === 200) {
        setSubmitState(SUCCEED);
        fetchData();
        reloadMyInfo();
        closeModal();
      } else {
        setSubmitState(FAILED);
        setWithdrawErrorMsg(parsedResponse.message);
      }

      window.setTimeout(() => {
        setSubmitState(ACTIVE);
      }, 1000);
    },
    [withdrawSelectedAmount],
  );

  const handleRefreshStatus = React.useCallback(async () => {
    if (isRefreshInCooldown) return;

    setRefreshError("");
    setRefreshLoading(true);
    try {
      const { parsedResponse, statusCode } = await apiPost(
        "/api/v1/users/affiliators/withdraw/refresh_status",
        {}
      );
      if (statusCode === 200) {
        const previous = readRefreshState();
        const count = ((previous && previous.count) || 0) + 1;
        const delaySec =
          REFRESH_COOLDOWNS_SEC[
            Math.min(count - 1, REFRESH_COOLDOWNS_SEC.length - 1)
          ];
        const nextAllowedAt = Date.now() + delaySec * 1000;
        writeRefreshState({
          count,
          nextAllowedAt,
          lastAttemptAt: Date.now(),
        });
        setRefreshCooldownUntil(nextAllowedAt);
        reloadMyInfo();
      } else {
        const message =
          parsedResponse && typeof parsedResponse.message === "string"
            ? parsedResponse.message
            : "Gagal memperbarui status penarikan.";
        setRefreshError(message);

        const retryAfter =
          parsedResponse &&
          typeof parsedResponse.retry_after_seconds === "number"
            ? parsedResponse.retry_after_seconds
            : null;
        if (retryAfter && retryAfter > 0) {
          const nextAllowedAt = Date.now() + retryAfter * 1000;
          const previous = readRefreshState();
          writeRefreshState({
            count: (previous && previous.count) || 1,
            nextAllowedAt,
            lastAttemptAt: Date.now(),
          });
          setRefreshCooldownUntil(nextAllowedAt);
        }
      }
    } catch {
      setRefreshError("Terjadi kesalahan saat memperbarui status.");
    } finally {
      setRefreshLoading(false);
    }
  }, [isRefreshInCooldown, reloadMyInfo]);

  return (
    <MetricContainer>
      <Card>
        <div>
          <CardTitle>Referensi</CardTitle>
          <CardMetric>{myInfo.count || "-"}</CardMetric>
        </div>
        <div>
          <ComparePercentage
            {...(myInfo.count_performa < 0
              ? {
                  $down: true,
                }
              : myInfo.count_performa > 0 && {
                  $up: true,
                })}
          >
            {myInfo.count_performa < 0 ? (
              <img
                src={unmodule(
                  require("../../images/icons/angle-double-down.svg"),
                )}
              />
            ) : (
              myInfo.count_performa > 0 && (
                <img
                  src={unmodule(
                    require("../../images/icons/angle-double-up.svg"),
                  )}
                />
              )
            )}
            <span>{myInfo.count_performa}%</span>
          </ComparePercentage>
          <CompareText>Since last month</CompareText>
        </div>
      </Card>
      <Card>
        <div>
          <CardTitle>Total Komisi</CardTitle>
          <CardMetric>{myInfo.comission_revenue || "-"}</CardMetric>
        </div>
        <div>
          <ComparePercentage
            {...(myInfo.comission_revenue_performa < 0
              ? {
                  $down: true,
                }
              : myInfo.comission_revenue_performa > 0 && {
                  $up: true,
                })}
          >
            {myInfo.comission_revenue_performa < 0 ? (
              <img
                src={unmodule(
                  require("../../images/icons/angle-double-down.svg"),
                )}
              />
            ) : (
              myInfo.comission_revenue_performa > 0 && (
                <img
                  src={unmodule(
                    require("../../images/icons/angle-double-up.svg"),
                  )}
                />
              )
            )}
            <span>{myInfo.comission_revenue_performa}%</span>
          </ComparePercentage>
          <CompareText>Since last month</CompareText>
        </div>
      </Card>
      <Card>
        <div>
          <CardTitle>Penarikan berhasil</CardTitle>
          <CardMetric>{myInfo.total_withdrawal || "-"}</CardMetric>
        </div>
        <div>
          <CompareText></CompareText>
        </div>
      </Card>
      <Card>
        <div>
          <CardTitle>
            {myInfo.upcoming_withdrawal
              ? myInfo.upcoming_withdrawal.status === "Menunggu Persetujuan"
                ? "Penarikan menunggu persetujuan"
                : "Penarikan menunggu pembayaran"
              : "Belum ada penarikan"}
          </CardTitle>
          <CardMetric>
            {myInfo.upcoming_withdrawal?.amount_text || "-"}
          </CardMetric>
        </div>
        <div>
          {myInfo.upcoming_withdrawal?.created_at ? (
            <React.Fragment>
              <ComparePercentage $info>
                <HoveredAlert
                  {...ALERT_INFO}
                  content={
                    <span>
                      {myInfo.upcoming_withdrawal.status === "Menunggu Persetujuan" && (
                        <UnwrappedText>
                          Permintaan penarikan Anda sedang{" "}
                          <b>menunggu persetujuan</b> dari admin.
                          <br />
                          <br />
                        </UnwrappedText>
                      )}
                      {myInfo.upcoming_withdrawal.payout_expiration_at && (
                        <UnwrappedText>
                          Link pembayaran kadaluwarsa pada:
                          <br />
                          <b>
                            {myInfo.upcoming_withdrawal.payout_expiration_at}
                          </b>
                          <br />
                          <br />
                        </UnwrappedText>
                      )}
                      <UnwrappedText>
                        Dibuat pada: <br />
                        <b>{myInfo.upcoming_withdrawal.created_at}</b>
                      </UnwrappedText>
                    </span>
                  }
                >
                  {myInfo.upcoming_withdrawal?.status}
                </HoveredAlert>
              </ComparePercentage>
              {myInfo.upcoming_withdrawal?.status === "Menunggu" && (
                <React.Fragment>
                  <RefreshButton
                    onClick={handleRefreshStatus}
                    disabled={refreshLoading || isRefreshInCooldown}
                  >
                    {refreshLoading
                      ? "Memuat..."
                      : isRefreshInCooldown
                      ? `⟳ Tunggu ${refreshSecondsLeft}s`
                      : "⟳ Refresh Status"}
                  </RefreshButton>
                  {refreshError && <Alert>{refreshError}</Alert>}
                </React.Fragment>
              )}
              {myInfo.upcoming_withdrawal?.payout_url && (
                <PayoutLink href={myInfo.upcoming_withdrawal.payout_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={unmodule(
                      require("../../images/icons/money-bill-transfer.svg"),
                    )}
                  />
                  Buka Link Pembayaran
                </PayoutLink>
              )}
            </React.Fragment>
          ) : (
            <WithdrawButtonWrapper>
              {myInfo.balance_amount < 500000 ? (
                <HoveredAlert
                  {...ALERT_INFO}
                  content={
                    <span>
                      <WrappedText>
                        • Pengguna hanya dapat melakukan penarikan ketika saldo{" "}
                        <b>&gt;= Rp500.000,-</b>
                        <br />
                        <br />
                      </WrappedText>
                      <WrappedText>
                        • Jumlah penarikan harus merupakan{" "}
                        <b>kelipatan Rp500.000,-</b>
                      </WrappedText>
                    </span>
                  }
                >
                  <WithdrawButton aria-disabled="true" tabIndex="0">
                    <img
                      src={unmodule(
                        require("../../images/icons/money-bill-transfer.svg"),
                      )}
                    />
                    Tarik Dana
                  </WithdrawButton>
                </HoveredAlert>
              ) : (
                <Popupable
                  title="Permintaan Penarikan Dana"
                  body={
                    <React.Fragment>
                      <WithdrawalNotes>
                        Jumlah penarikan harus merupakan{" "}
                        <HighlightedOrange>
                          kelipatan Rp500.000,-
                        </HighlightedOrange>{" "}
                        dan{" "}
                        <HighlightedOrange>
                          tidak lebih besar dari jumlah saldo yang dimiliki
                        </HighlightedOrange>
                      </WithdrawalNotes>
                      <table>
                        <tbody>
                          <tr>
                            <th>
                              Saldo saat ini
                              <h2>{myInfo.balance}</h2>
                            </th>
                          </tr>
                        </tbody>
                        <tbody></tbody>
                      </table>
                      <WithdrawAmountTitle>
                        Pilih kelipatan penarikan:
                      </WithdrawAmountTitle>
                      <WithdrawAmountFlex>
                        {withdrawAmounts.map((val) => (
                          <WithdrawTypedButton
                            key={val.label}
                            type="button"
                            $active={val.$active}
                            disabled={val.disabled}
                            onClick={() => {
                              setWithdrawState(
                                produce((draft) => {
                                  for (const key in draft) {
                                    delete draft[key];
                                  }
                                  draft[`${val.amount}`] = true;
                                }),
                              );
                            }}
                          >
                            {val.label}
                          </WithdrawTypedButton>
                        ))}
                        <WithdrawCustomAmount
                          type="text"
                          onChange={handleChangeWithdrawAmount}
                          value={withdrawState.custom || ""}
                          $active={
                            withdrawState.custom || withdrawState.custom === ""
                          }
                          onClick={() => {
                            setWithdrawState(
                              produce((draft) => {
                                for (const key in draft) {
                                  delete draft[key];
                                }
                                draft["custom"] = "";
                              }),
                            );
                          }}
                        />
                      </WithdrawAmountFlex>
                      <WithdrawSeparatorInside />
                      {withdrawErrorMsg && (
                        <React.Fragment>
                          <Alert>{withdrawErrorMsg}</Alert>
                        </React.Fragment>
                      )}
                      <WithdrawButtonInside
                        {...(submitState === ACTIVE && withdrawSelectedAmount
                          ? {
                              onClick: handleWithdrawInsideClick,
                            }
                          : {
                              disabled: true,
                            })}
                      >
                        {withdrawSelectedAmount && (
                          <img
                            src={unmodule(
                              require("../../images/icons/money-bill-transfer.svg"),
                            )}
                          />
                        )}
                        {submitState === SUBMITTING ? (
                          "Loading..."
                        ) : submitState === SUCCEED ? (
                          "Berhasil"
                        ) : submitState === FAILED ? (
                          "Gagal."
                        ) : (
                          <React.Fragment>
                            <div>
                              Ajukan Penarikan Dana
                              {withdrawSelectedAmount && (
                                <AmountToWithdrawText>
                                  Rp
                                  {addThousandSeparator(
                                    `${withdrawSelectedAmount}`,
                                  )}
                                </AmountToWithdrawText>
                              )}
                            </div>
                          </React.Fragment>
                        )}
                      </WithdrawButtonInside>
                    </React.Fragment>
                  }
                >
                  <WithdrawButton>
                    <img
                      src={unmodule(
                        require("../../images/icons/money-bill-transfer.svg"),
                      )}
                    />
                    Tarik Dana
                  </WithdrawButton>
                </Popupable>
              )}
            </WithdrawButtonWrapper>
          )}
        </div>
      </Card>
      {props.children}
    </MetricContainer>
  );
};
export default UnnamedComponent;

const CompareText = styled.div`
  color: #898989;
  font-size: 14px;
  margin-top: 10px;
`;

const ComparePercentage = styled.div`
  align-items: center;
  background-color: #a2a2a236;
  border-radius: 5px;
  color: #c7c7c7;
  display: inline-flex;
  padding: 3px 7px;

  ${(props) =>
    props.$up &&
    css`
      background-color: #00a90e36;
      color: #20bd10;

      & > span {
        margin-left: 5px;
      }
    `};

  ${(props) =>
    props.$down &&
    css`
      background-color: #a9000036;
      color: #dd1313;

      & > span {
        margin-left: 5px;
      }
    `};

  ${(props) =>
    props.$info &&
    css`
      background-color: #a9400036;
      color: #dd5713;
    `};
`;

const CardTitle = styled.div`
  font-weight: bold;
  margin-bottom: 15px;
`;

const CardMetric = styled.div`
  font-size: 30px;
  font-weight: bold;
`;

const Card = styled.div`
  background-color: #2e2e33;
  border-radius: 5px;
  box-shadow: 0px 0px 1px rgb(255 255 255);
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: space-between;
  padding: 20px;
`;

const MetricContainer = styled.div`
  display: grid;
  gap: 20px; /* spacing between cards */
  grid-template-columns: repeat(3, 1fr); /* 3-column grid */
  grid-template-rows: auto auto; /* 2 rows */
  margin: 0 auto;
  padding: 20px 0;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: unset;
  }
`;

const WithdrawButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const WithdrawButton = styled.button`
  align-items: center;
  background-color: #009688;
  border-radius: 2px;
  color: inherit;
  display: flex;
  font-weight: bold;
  gap: 5px;
  justify-content: center;
  margin-top: 5px;
  padding: 10px 0;
  width: 100%;
  border: none;

  &:hover:not(:disabled),
  &:hover:not([aria-disabled="true"]),
  &:active:not(:disabled),
  &:active:not([aria-disabled="true"]) {
    cursor: pointer;
    transform: translate(1px, 1px); /* Simple press effect */
  }

  &:disabled, &[aria-disabled="true"] {
    background-color: #757575;
    color: #929292;
    cursor: not-allowed;
    opacity: 0.7;
  }

  & > img {
    width: 24px;
  }
`;

const WithdrawSeparatorInside = styled.div`
  background-color: #717171;
  height: 1px;
  margin: 20px 0;
`;

const WithdrawButtonInside = styled(WithdrawButton)`
  &[disabled] {
    background-color: #757575;
    color: #929292;
  }
`;

const WithdrawalNotes = styled.div`
  margin-bottom: 20px;
`;

const HighlightedOrange = styled.span`
  font-weight: bold;
  color: ${orangish};
`;

const WithdrawAmountTitle = styled.div`
  margin: 30px 0 20px;
  font-weight: bold;
`;

const WithdrawAmountFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
`;

const cssWithdrawTypedButton = css`
  background-color: #757575;
  border-radius: 7px;
  color: white;
  flex: 0 0 calc(50% - 20px);
  font-size: 20px;
  padding: 20px 10px;

  &[disabled] {
    background-color: #494949;
    color: #8a8a8a;
    cursor: not-allowed;
  }

  &:hover,
  &:active,
  &:focus {
    background-color: #405452;
    border: 2px solid #009688;
  }

  ${(props) =>
    props.$active &&
    css`
      background-color: #009688;
    `};
`;

const WithdrawTypedButton = styled.button`
  ${cssWithdrawTypedButton};
`;

const WithdrawCustomAmount = styled.input`
  ${cssWithdrawTypedButton};
  flex: 0 0 calc(50% - 45px);
`;

const AmountToWithdrawText = styled.div`
  font-size: 20px;
  margin-top: 5px;
`;

const UnwrappedText = styled.div`
  text-wrap: nowrap;
`;

const WrappedText = styled.div`
  white-space: wrap;
`;

const PayoutLink = styled.a`
  align-items: center;
  background-color: #009688;
  border-radius: 2px;
  color: white;
  display: flex;
  font-weight: bold;
  gap: 5px;
  justify-content: center;
  margin-top: 10px;
  padding: 10px 0;
  text-decoration: none;
  width: 100%;

  &:hover,
  &:active {
    background-color: #00796b;
    cursor: pointer;
    transform: translate(1px, 1px);
  }

  & > img {
    width: 24px;
  }
`;

const RefreshButton = styled.button`
  align-items: center;
  background-color: #2196F3;
  border-radius: 2px;
  border: none;
  color: white;
  display: flex;
  font-weight: bold;
  gap: 5px;
  justify-content: center;
  margin-top: 10px;
  padding: 10px 0;
  width: 100%;

  &:hover:not([disabled]),
  &:active:not([disabled]) {
    background-color: #1976D2;
    cursor: pointer;
    transform: translate(1px, 1px);
  }

  &[disabled] {
    background-color: #90CAF9;
    color: #E3F2FD;
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${props => props.disabled && css`
    &::before {
      content: "⟳";
      display: inline-block;
      margin-right: 5px;
      animation: spin 1s linear infinite;
    }
  `}

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
